import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import { NotificationProvider } from "./contexts/NotificationContext";

import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import OnboardingWizard from "./pages/OnboardingWizard";
import ClientDashboard from "./pages/ClientDashboard";
import SessionView from "./pages/SessionView";
import InterpreterDashboard from "./pages/InterpreterDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminOrgsPage from "./pages/AdminOrgsPage";
import AdminClientsPage from "./pages/AdminClientsPage";
import AdminInterpretersPage from "./pages/AdminInterpretersPage";
import AdminFeeSchedulesPage from "./pages/AdminFeeSchedulesPage";
import AdminPayRatesPage from "./pages/AdminPayRatesPage";
import AdminSessionsPage from "./pages/AdminSessionsPage";
import AdminSlaPage from "./pages/AdminSlaPage";
import AdminLedgerPage from "./pages/AdminLedgerPage";
import AdminPoliciesPage from "./pages/AdminPoliciesPage";
import AdminTemplatePage from "./pages/AdminFormsPage";
import AdminLanguagesPage from "./pages/AdminLanguagesPage";
import AdminCreditsPage from "./pages/AdminCreditsPage";
import AdminLanguageRequestsPage from "./pages/AdminLanguageRequestsPage";
import AdminInvoicesPage from "./pages/AdminInvoicesPage";
import AdminSecurityProfilesPage from "./pages/AdminSecurityProfilesPage";
import AdminAuditLogPage from "./pages/AdminAuditLogPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminCompliancePage from "./pages/AdminCompliancePage";
import AdminBlogPage from "./pages/AdminBlogPage";
import AdminCallSettingsPage from "./pages/AdminCallSettingsPage";
import AdminAIModelsPage from "./pages/AdminAIModelsPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import FinanceDashboard from "./pages/FinanceDashboard";
import HRDashboard from "./pages/HRDashboard";
import ReferralPage from "./pages/ReferralPage";
import OrgAdminDashboard from "./pages/OrgAdminDashboard";
import ReportBuilderPage from "./pages/ReportBuilderPage";
import DashboardBuilderPage from "./pages/DashboardBuilderPage";
import ScheduledReportsPage from "./pages/ScheduledReportsPage";

// Public SEO Pages
import HomePage from "./pages/public/HomePage";
import AboutPage from "./pages/public/AboutPage";
import ContactPage, { BookInterpreterPage, BookDemoPage } from "./pages/public/ContactPage";
import HowItWorksPage from "./pages/public/HowItWorksPage";
import PricingPage from "./pages/public/PricingPage";
import SecurityPage from "./pages/public/SecurityPage";
import LanguagesHubPage from "./pages/public/LanguagesHubPage";
import LocationsHubPage from "./pages/public/LocationsHubPage";
import ServicePage from "./pages/public/ServicePage";
import IndustryPage from "./pages/public/IndustryPage";
import CityPage from "./pages/public/CityPage";
import LanguagePage from "./pages/public/LanguagePage";
import HybridPage from "./pages/public/HybridPage";
import ServiceLocationPage from "./pages/public/ServiceLocationPage";
import BlogListPage from "./pages/public/BlogListPage";
import BlogPostPage from "./pages/public/BlogPostPage";

function ProtectedRoute({ children, allowedRoles, requirePermission }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (requirePermission) {
    const perms = user.permissions || [];
    if (user.role !== 'admin' && !perms.includes(requirePermission)) {
      return <Navigate to={getHomeRoute(user)} replace />;
    }
    return children;
  }
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getHomeRoute(user)} replace />;
  }
  return children;
}

function getHomeRoute(user) {
  if (!user) return '/auth';
  if (user.role === 'admin') return '/admin';
  if (user.role === 'interpreter') return '/interpreter';
  if (user.role === 'org_admin') return '/org-admin';
  const profiles = user.security_profiles || [];
  if (profiles.includes('Finance')) return '/finance';
  if (profiles.includes('Human Resources')) return '/hr';
  return '/client';
}

function AuthGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-[hsl(var(--brand))] border-t-transparent rounded-full animate-spin" /></div>;
  if (user) return <Navigate to={getHomeRoute(user)} replace />;
  return <AuthPage />;
}

// Resolves programmatic SEO slugs like:
//   interpreting-services-new-york  → CityPage
//   spanish-interpreter-services    → LanguagePage
//   spanish-interpreter-new-york    → HybridPage
function SEOPageResolver() {
  const { pageSlug } = useParams();

  if (pageSlug.startsWith('interpreting-services-')) {
    const citySlug = pageSlug.replace('interpreting-services-', '');
    return <CityPage citySlugProp={citySlug} />;
  }

  if (pageSlug.endsWith('-interpreter-services')) {
    const langSlug = pageSlug.replace('-interpreter-services', '');
    return <LanguagePage langSlugProp={langSlug} />;
  }

  const hybridMatch = pageSlug.match(/^(.+)-interpreter-(.+)$/);
  if (hybridMatch) {
    return <HybridPage langSlugProp={hybridMatch[1]} citySlugProp={hybridMatch[2]} />;
  }

  return <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* ── Public SEO Pages ── */}
      <Route path="/" element={<HomePage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/book-interpreter" element={<BookInterpreterPage />} />
      <Route path="/book-demo" element={<BookDemoPage />} />
      <Route path="/languages" element={<LanguagesHubPage />} />
      <Route path="/locations" element={<LocationsHubPage />} />
      <Route path="/services/:serviceSlug/:citySlug" element={<ServiceLocationPage />} />
      <Route path="/services/:serviceSlug" element={<ServicePage />} />
      <Route path="/industries/:industrySlug" element={<IndustryPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* ── Auth ── */}
      <Route path="/auth" element={<AuthGate />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/billing-complete" element={<AuthGate />} />

      {/* ── Protected: Client ── */}
      <Route path="/client/onboarding" element={<ProtectedRoute allowedRoles={['client']}><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/client" element={<ProtectedRoute allowedRoles={['client']}><ClientDashboard /></ProtectedRoute>} />
      <Route path="/client/session/:id" element={<ProtectedRoute allowedRoles={['client']}><SessionView /></ProtectedRoute>} />

      {/* ── Protected: Interpreter ── */}
      <Route path="/interpreter" element={<ProtectedRoute allowedRoles={['interpreter']}><InterpreterDashboard /></ProtectedRoute>} />

      {/* ── Protected: Admin ── */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/orgs" element={<ProtectedRoute requirePermission="manage_orgs"><AdminOrgsPage /></ProtectedRoute>} />
      <Route path="/admin/clients" element={<ProtectedRoute requirePermission="manage_clients"><AdminClientsPage /></ProtectedRoute>} />
      <Route path="/admin/interpreters" element={<ProtectedRoute requirePermission="manage_interpreters"><AdminInterpretersPage /></ProtectedRoute>} />
      <Route path="/admin/languages" element={<ProtectedRoute requirePermission="manage_languages"><AdminLanguagesPage /></ProtectedRoute>} />
      <Route path="/admin/language-requests" element={<ProtectedRoute requirePermission="manage_language_requests"><AdminLanguageRequestsPage /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute requirePermission="manage_invoices"><AdminInvoicesPage /></ProtectedRoute>} />
      <Route path="/admin/fee-schedules" element={<ProtectedRoute requirePermission="manage_fee_schedules"><AdminFeeSchedulesPage /></ProtectedRoute>} />
      <Route path="/admin/pay-rates" element={<ProtectedRoute requirePermission="manage_pay_rates"><AdminPayRatesPage /></ProtectedRoute>} />
      <Route path="/admin/credits" element={<ProtectedRoute allowedRoles={['admin']}><AdminCreditsPage /></ProtectedRoute>} />
      <Route path="/admin/sessions" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminSessionsPage /></ProtectedRoute>} />
      <Route path="/admin/sla" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminSlaPage /></ProtectedRoute>} />
      <Route path="/admin/ledger" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminLedgerPage /></ProtectedRoute>} />
      <Route path="/admin/policies" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminPoliciesPage /></ProtectedRoute>} />
      <Route path="/admin/template" element={<ProtectedRoute allowedRoles={['admin']}><AdminTemplatePage /></ProtectedRoute>} />
      <Route path="/admin/forms" element={<ProtectedRoute allowedRoles={['admin']}><AdminTemplatePage /></ProtectedRoute>} />
      <Route path="/admin/security-profiles" element={<ProtectedRoute allowedRoles={['admin']}><AdminSecurityProfilesPage /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute allowedRoles={['admin']}><AdminAuditLogPage /></ProtectedRoute>} />
      <Route path="/admin/call-settings" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminCallSettingsPage /></ProtectedRoute>} />
      <Route path="/admin/ai-models" element={<ProtectedRoute allowedRoles={['admin']}><AdminAIModelsPage /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute allowedRoles={['admin', 'org_admin']}><AdminLeadsPage /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsersPage /></ProtectedRoute>} />
      <Route path="/admin/compliance" element={<ProtectedRoute allowedRoles={['admin']}><AdminCompliancePage /></ProtectedRoute>} />
      <Route path="/admin/blog" element={<ProtectedRoute allowedRoles={['admin']}><AdminBlogPage /></ProtectedRoute>} />

      {/* ── Protected: Finance / HR / Org Admin ── */}
      <Route path="/finance" element={<ProtectedRoute requirePermission="manage_invoices"><FinanceDashboard /></ProtectedRoute>} />
      <Route path="/hr" element={<ProtectedRoute requirePermission="manage_interpreters"><HRDashboard /></ProtectedRoute>} />
      <Route path="/referral" element={<ProtectedRoute allowedRoles={['admin', 'org_admin', 'client']}><ReferralPage /></ProtectedRoute>} />
      <Route path="/org-admin" element={<ProtectedRoute allowedRoles={['org_admin', 'admin']}><OrgAdminDashboard /></ProtectedRoute>} />

      {/* ── Protected: Reports & Dashboards ── */}
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'org_admin', 'client', 'interpreter', 'finance', 'hr']}><ReportBuilderPage /></ProtectedRoute>} />
      <Route path="/reports/schedules" element={<ProtectedRoute allowedRoles={['admin', 'org_admin', 'client', 'interpreter', 'finance', 'hr']}><ScheduledReportsPage /></ProtectedRoute>} />
      <Route path="/dashboards" element={<ProtectedRoute allowedRoles={['admin', 'org_admin', 'client', 'interpreter', 'finance', 'hr']}><DashboardBuilderPage /></ProtectedRoute>} />

      {/* ── Programmatic SEO slug resolver (must be last single-segment catch) ── */}
      <Route path="/:pageSlug" element={<SEOPageResolver />} />

      {/* ── Fallback ── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <NotificationProvider>
            <AppRouter />
            <Toaster />
          </NotificationProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
